import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import FormInputComponent from 'webapp/components/form/input';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type { TrackingOriginTypeForTrialPopupOpened } from 'webapp/services/billing';

export default class BillingPopupFreeTrialRequestComponent extends PopupInnerModalComponent<PopupData.Billing.FreeTrialRequest> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('billing') declare billingService: Service.Billing;
	@service('request') declare requestService: Service.Request;

	@tracked trialObjective = '';
	origin: TrackingOriginTypeForTrialPopupOpened | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.origin =
			this.popupData.origin ||
			this.billingService.getOriginByFeatureForTrialPopupOpened(this.popupData.feature);
		this.analyticsService.event(TrackingEvent.TRIAL_POPUP_OPENED, {
			originUrl: window.location.href,
			origin: this.origin,
		});
	}

	get trialFeatures(): string[] {
		const trialFeatures = [];

		switch (this.popupData.feature) {
			case BillingFeature.QB:
				trialFeatures.push('Create custom metrics with Metric Builder');
				break;
			case BillingFeature.SCRIPTS:
				trialFeatures.push('Create calculated metrics with Data Calculations');
				break;
			case BillingFeature.CUSTOM_RANGE:
				trialFeatures.push('View more historical data');
				break;
			case BillingFeature.BOARDS:
				trialFeatures.push('Build more Databoards');
				break;
			case BillingFeature.SOURCES:
				trialFeatures.push('Connect more Data Sources');
				break;
			case BillingFeature.USERS:
				trialFeatures.push('Add more users');
				break;
			case BillingFeature.ADVANCED_REPORTS:
				trialFeatures.push('Use advanced slide-type');
				break;
			case BillingFeature.API_METRIC_BUILDER:
				trialFeatures.push('Use Custom API Integrations');
				break;
			case BillingFeature.ALERTS:
				trialFeatures.push('Create Alerts');
				break;
			case BillingFeature.GOALS:
				trialFeatures.push('Create Goals');
				break;
			case BillingFeature.REPORTS_BASIC:
				trialFeatures.push('Create Reports');
				break;
			case BillingFeature.DATA_PUSH:
				trialFeatures.push('Push Custom Data');
				break;
			case BillingFeature.WORKFLOW_INTEGRATIONS:
				trialFeatures.push('Workflow Intergations');
				break;
			case BillingFeature.FISCAL_CALENDARS:
				trialFeatures.push('Fiscal Calendars');
				break;
			case BillingFeature.ACCOUNT_TEMPLATES:
				trialFeatures.push('Create Account Templates');
				break;
		}

		return trialFeatures;
	}

	get text(): string {
		const freePlanFeatures = this.billingService.findFreePlan()?.features;
		let text = '';

		const formatText = (
			featureLabel: string,
			featureCountMax: number,
			action = 'connect',
		): string => {
			return `The Free plan is limited to ${featureCountMax} ${featureLabel}. To ${action} ${featureLabel}, begin your free 14-day trial period or upgrade your plan.`;
		};

		if (freePlanFeatures) {
			switch (this.popupData.feature) {
				case BillingFeature.BOARDS:
					text = formatText('dashboards', freePlanFeatures.boardsMax!);
					break;
				case BillingFeature.SOURCES:
					text = formatText('data sources', freePlanFeatures.sourcesMax!);
					break;
				case BillingFeature.USERS:
					text = formatText('users', freePlanFeatures.usersMax!, 'invite');
					break;
				default:
					text =
						'You are trying to access a feature that is only available on paid plans.';
					break;
			}
		}

		return text;
	}

	@action
	requestTrial(): void {
		this.submitInProgress = true;

		const planForTrial = this.billingService.getPlanForTrial(
			this.popupData.feature as BillingFeature,
		);
		if (planForTrial) {
			this.billingService
				.doFirstTrialRequest(
					this.origin,
					this.trialFeatures,
					planForTrial,
					this.trialObjective,
				)
				.then(() => {
					this.close();
				})
				.finally(() => {
					this.submitInProgress = false;
				})
				.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
		}
	}
	<template>
		<div class="content">

			<img src="/images/modal/databoard.png" width="352" height="190" alt />
			<h2>Activate your 14-day free trial</h2>
			<div class="under-h2">100% FREE, NO CREDIT CARD REQUIRED</div>
			<p>{{this.text}}</p>
			<FormInputComponent
				name="trial_objective"
				placeholder="Need a little guidance? Tell us more about what you’d like to accomplish…"
				maxlength="500"
				@value={{this.trialObjective}}
				@hideFloatLabel={{true}}
			/>

			<div class="controls2 controls2--center">
				<SubmitButtonComponent
					@text="Start my 14-day free trial"
					@size="big"
					@noDelay={{true}}
					@onTrigger={{this.requestTrial}}
					@submitInProgress={{this.submitInProgress}}
				/>
				<SubmitButtonComponent
					@text="No thanks, I’ll stick with the current plan"
					@theme="grey-empty"
					@onTrigger={{this.close}}
				/>
			</div>

		</div>
	</template>
}
